import type { MfaSetupResponse, MfaVerifyResponse } from '@dnx/contracts';
import { Alert, Button, Field, OtpInput, Spinner } from '@dnx/ui';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Check, Copy } from 'lucide-react';
import QRCode from 'qrcode';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { AuthLayout } from '../components/auth-layout.js';
import { api, ApiError } from '../lib/api.js';
import { isAuthError, pendingStep, recoveryCodesStore } from '../lib/session.js';

function Step({ n, title, children }: { n: number; title: string; children?: ReactNode }) {
  return (
    <li className="flex gap-3.5">
      <span className="grid size-6 shrink-0 place-items-center rounded-full bg-primary-soft text-xs font-semibold text-primary">{n}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-fg">{title}</p>
        {children && <div className="mt-2">{children}</div>}
      </div>
    </li>
  );
}

export function MfaSetupPage() {
  const navigate = useNavigate();
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);

  const setup = useMutation({
    mutationFn: () => api<MfaSetupResponse>('/auth/mfa/setup', { method: 'POST' }),
    onSuccess: async (res) => setQr(await QRCode.toString(res.otpauthUri, { type: 'svg', margin: 0, errorCorrectionLevel: 'M' })),
    onError: (e) => { if (isAuthError(e)) void navigate({ to: '/entrar' }); },
  });
  const verify = useMutation({
    mutationFn: (c: string) => api<MfaVerifyResponse>('/auth/mfa/verify', { method: 'POST', body: { code: c } }),
    onSuccess: (res) => {
      pendingStep.set(null);
      recoveryCodesStore.set(res.recoveryCodes ?? null);
      void navigate({ to: '/entrar/codigos-de-recuperacao' });
    },
    onError: (e) => {
      setCode('');
      if (e instanceof ApiError && e.code === 'SESSION_EXPIRED') void navigate({ to: '/entrar' });
    },
  });

  // Um único segredo por tela: StrictMode roda efeitos duas vezes em dev, e dois segredos
  // fariam o QR exibido divergir do que ficou na sessão.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    setup.mutate();
  }, [setup]);

  const secretGroups = setup.data?.secret.match(/.{1,4}/g)?.join(' ') ?? '';

  return (
    <AuthLayout
      title="Proteja sua conta"
      description="A verificação em duas etapas é obrigatória para quem acessa dados de pacientes. Leva menos de um minuto."
    >
      <ol className="flex flex-col gap-6">
        <Step n={1} title="Instale um aplicativo autenticador">
          <p className="text-[13px] text-fg-muted">Google Authenticator, Microsoft Authenticator ou 1Password.</p>
        </Step>
        <Step n={2} title="Escaneie o QR code com o aplicativo">
          <div className="flex items-start gap-4">
            <div className="grid size-40 shrink-0 place-items-center rounded-lg border border-border bg-white p-3">
              {qr ? <div className="size-full [&_svg]:size-full" role="img" aria-label="QR code para o aplicativo autenticador" dangerouslySetInnerHTML={{ __html: qr }} />
                : <Spinner className="size-6 text-fg-subtle" label="Gerando QR code" />}
            </div>
            <details className="text-[13px] text-fg-muted">
              <summary className="cursor-pointer select-none text-primary hover:underline">Não consegue escanear?</summary>
              <p className="mt-2">Digite esta chave no aplicativo:</p>
              <div className="mt-1.5 flex items-center gap-1.5">
                <code className="rounded bg-surface-muted px-2 py-1 font-mono text-xs break-all text-fg">{secretGroups}</code>
                <Button variant="ghost" size="icon" aria-label="Copiar chave"
                  onClick={() => { void navigator.clipboard.writeText(setup.data?.secret ?? ''); setCopied(true); }}>
                  {copied ? <Check className="size-4 text-success" /> : <Copy className="size-4" />}
                </Button>
              </div>
            </details>
          </div>
        </Step>
        <Step n={3} title="Digite o código de 6 dígitos que aparece no aplicativo">
          <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (code.length === 6) verify.mutate(code); }}>
            <Field label="Código do autenticador" error={verify.error?.message}>
              <OtpInput value={code} onValueChange={(v) => { setCode(v); if (v.length === 6) verify.mutate(v); }} disabled={!qr || verify.isPending} />
            </Field>
            <Button type="submit" size="lg" block loading={verify.isPending} disabled={code.length !== 6}>Ativar e continuar</Button>
          </form>
        </Step>
      </ol>
      {setup.error && !isAuthError(setup.error) && <Alert tone="danger" className="mt-6" title="Não foi possível gerar o QR code">{setup.error.message}</Alert>}
    </AuthLayout>
  );
}
